import { tunaEvent } from "@app/tuna";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Custom hook for syncing an input value with a URL query param
 *
 * This hook stores state locally and syncs to URL params with a debounce, which
 * prevents the cursor from jumping when the user types.
 *
 * @param paramName - The URL param name to sync with (default: "search")
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 * @param tunaKey - Optional tuna event name to fire when params change
 * @returns Object with search value and onChange handler
 *
 * @example
 * const { search, onChange } = useSyncedQueryParam();
 * return <InputSearch search={search} onChange={onChange} />
 *
 * @example
 * const { search, onChange } = useSyncedQueryParam(
 *   "q",
 *   650,
 *   "activity-filter-query",
 * );
 */
export function useSyncedQueryParam(
  paramName = "search",
  debounceMs = 300,
  tunaKey?: string,
): {
  search: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
} {
  const [params, setParams] = useSearchParams();
  const urlValue = params.get(paramName) || "";
  const [search, setSearch] = useState(params.get(paramName) || "");
  const prevUrlValueRef = useRef(params.get(paramName) || "");

  /** When the URL param changes without going through local state (back/forward, deep links), sync input state. */
  useEffect(() => {
    if (urlValue !== prevUrlValueRef.current) {
      prevUrlValueRef.current = urlValue;
      setSearch(urlValue);
    }
  }, [urlValue]);

  // Sync search state with URL params (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setParams(
        (prev) => {
          const currentParams = new URLSearchParams(prev);
          const currentSearchValue = currentParams.get(paramName);
          const nextSearchValue = search || "";

          if ((currentSearchValue || "") === nextSearchValue) {
            return currentParams;
          }

          if (nextSearchValue) {
            // Only reset page if search value actually changed
            currentParams.delete("page");

            if (tunaKey && currentSearchValue) {
              tunaEvent(tunaKey, nextSearchValue);
            }

            currentParams.set(paramName, nextSearchValue);
          } else {
            currentParams.delete(paramName);
            // Only reset page if search was just cleared (had a value before)
            if (currentSearchValue) {
              currentParams.delete("page");
            }
          }
          prevUrlValueRef.current = nextSearchValue;
          return currentParams;
        },
        { replace: true },
      );
    }, debounceMs);

    return () => {
      return clearTimeout(timeoutId);
    };
  }, [search, paramName, debounceMs, setParams, tunaKey]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value);
  };

  return { search, onChange };
}
